// components/product/BenefitGridBit.jsx
import React from 'react';
import { motion } from 'framer-motion';
import { Reveal, StaggerContainer } from '../animations/Reveal';
import {
    FaSun,
    FaShieldAlt,
    FaTint,
    FaLeaf,
    FaStar,
    FaGem
} from 'react-icons/fa';
import '../../styles/globals.css';

const BenefitGridBit = ({ benefits }) => {
    const iconMap = {
        sun: FaSun,
        shield: FaShieldAlt,
        droplet: FaTint,
        leaf: FaLeaf,
        star: FaStar,
        gem: FaGem
    };

    return (
        <div className="space-y-16">
            {/* Header */}
            <div className="text-center space-y-4">
                <Reveal>
                    <div className="inline-flex items-center gap-2 px-4 py-2 bg-champagne/10 rounded-full">
                        <FaGem className="w-3 h-3 text-champagne" />
                        <span className="text-sm tracking-widest uppercase text-champagne">
                            Transformative Benefits
                        </span>
                    </div>
                </Reveal>

                <Reveal delay={0.2}>
                    <h2 className="text-4xl md:text-5xl font-heading font-light">
                        Where <span className="italic text-sage">Science</span> Meets{' '}
                        <span className="italic text-champagne">Sensation</span>
                    </h2>
                </Reveal>

                <Reveal delay={0.4}>
                    <p className="text-xl text-charcoal/60 max-w-2xl mx-auto">
                        Experience a new dimension of beauty with our meticulously crafted formulations
                    </p>
                </Reveal>
            </div>

            {/* Benefits Grid */}
            {benefits && benefits.length > 0 ? (
                <StaggerContainer stagger={0.1}>
                    <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
                        {benefits.map((benefit, index) => {
                            const Icon = iconMap[benefit.icon] || FaStar;

                            return (
                                <motion.div
                                    key={index}
                                    variants={{
                                        hidden: { opacity: 0, y: 40 },
                                        visible: { opacity: 1, y: 0 }
                                    }}
                                    whileHover={{ y: -10 }}
                                    className="group"
                                >
                                    <div className="relative h-full bg-white rounded-2xl p-8 shadow-subtle hover:shadow-lift transition-all duration-500">
                                        {/* Icon Background */}
                                        <div className="absolute top-8 left-8 w-16 h-16 rounded-2xl bg-gradient-to-br from-champagne/10 to-sage/10 group-hover:from-champagne/20 group-hover:to-sage/20 transition-all duration-500" />

                                        {/* Icon */}
                                        <div className="relative mb-6">
                                            <div className="w-12 h-12 rounded-xl bg-white shadow-soft flex items-center justify-center">
                                                <Icon className="w-5 h-5 text-champagne" />
                                            </div>
                                        </div>

                                        {/* Content */}
                                        <div className="space-y-4">
                                            <h3 className="text-xl font-medium text-charcoal">
                                                {benefit.title}
                                            </h3>
                                            <p className="text-charcoal/60">
                                                {benefit.description}
                                            </p>
                                        </div>

                                        {/* Hover Line */}
                                        <motion.div
                                            className="absolute bottom-0 left-0 right-0 h-1 bg-gradient-to-r from-champagne to-sage rounded-b-2xl"
                                            initial={{ scaleX: 0 }}
                                            whileHover={{ scaleX: 1 }}
                                            transition={{ duration: 0.4 }}
                                        />

                                        {/* Floating Animation */}
                                        <motion.div
                                            className="absolute inset-0 border-2 border-transparent rounded-2xl"
                                            animate={{
                                                borderColor: [
                                                    'rgba(212, 180, 131, 0)',
                                                    'rgba(212, 180, 131, 0.2)',
                                                    'rgba(212, 180, 131, 0)'
                                                ]
                                            }}
                                            transition={{
                                                duration: 3,
                                                repeat: Infinity,
                                                ease: "easeInOut"
                                            }}
                                        />
                                    </div>
                                </motion.div>
                            );
                        })}
                    </div>
                </StaggerContainer>
            ) : (
                <div className="text-center py-12">
                    <p className="text-charcoal/60">Benefits information coming soon</p>
                </div>
            )}
        </div>
    );
};

export default BenefitGridBit;